import React, { useState, useCallback } from 'react';
import { useRazorpay } from 'react-razorpay';
import { Button } from './ui/button';
import { Loader2, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PaymentButton Component
 * Handles Razorpay payment flow with mock support
 * 
 * @param {Object} props
 * @param {number} props.amount - Amount in INR (will be converted to paise)
 * @param {string} props.courseId - Course ID for enrollment
 * @param {string} props.tier - Pricing tier (self_learning or instructor_led)
 * @param {string} props.enrollmentId - Enrollment ID to update after payment
 * @param {string} props.couponCode - Optional coupon code
 * @param {Function} props.onSuccess - Callback on successful payment
 * @param {Function} props.onError - Callback on payment error
 * @param {string} props.buttonText - Custom button text
 * @param {string} props.className - Additional CSS classes
 */
export default function PaymentButton({
  amount,
  courseId,
  tier = 'self_learning',
  enrollmentId,
  couponCode = null,
  onSuccess,
  onError,
  buttonText = 'Pay Now',
  className = '',
  disabled = false,
  user = null
}) {
  const { Razorpay } = useRazorpay();
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'success', 'error'

  const handlePayment = useCallback(async () => {
    if (!amount || !courseId || !enrollmentId) {
      toast.error('Missing payment information');
      return;
    }

    setLoading(true);
    setPaymentStatus(null);

    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      // 1. Get payment config
      const configRes = await axios.get(`${API}/api/payments/config`, { headers });
      const config = configRes.data;

      // 2. Create order
      const orderRes = await axios.post(
        `${API}/api/payments/create-order`,
        {
          amount: amount * 100, // Convert to paise
          currency: 'INR',
          course_id: courseId,
          tier: tier,
          coupon_code: couponCode
        },
        { headers }
      );

      const orderData = orderRes.data;

      // Check if mock mode
      if (config.mock_mode || orderData.mock_mode) {
        // Simulate mock payment flow
        const mockPaymentId = `pay_mock_${Date.now()}`;
        const mockSignature = `mock_sig_${Date.now()}`;

        // Show mock payment dialog
        const confirmMock = window.confirm(
          `🧪 MOCK PAYMENT MODE\n\n` +
          `Amount: ₹${(orderData.amount / 100).toLocaleString('en-IN')}\n` +
          `${orderData.discount_applied > 0 ? `Discount: ₹${(orderData.discount_applied / 100).toLocaleString('en-IN')}\n` : ''}` +
          `\nClick OK to simulate successful payment\nClick Cancel to simulate failed payment`
        );

        if (confirmMock) {
          // Verify mock payment
          await axios.post(
            `${API}/api/payments/verify`,
            {
              razorpay_order_id: orderData.order_id,
              razorpay_payment_id: mockPaymentId,
              razorpay_signature: mockSignature,
              enrollment_id: enrollmentId
            },
            { headers }
          );

          setPaymentStatus('success');
          toast.success('Payment successful! Your dashboard is now unlocked.');
          onSuccess?.({
            paymentId: mockPaymentId,
            orderId: orderData.order_id,
            amount: orderData.amount
          });
        } else {
          throw new Error('Payment cancelled by user');
        }
      } else {
        // Real Razorpay payment flow
        const options = {
          key: config.key_id,
          amount: orderData.amount,
          currency: 'INR',
          name: config.name,
          description: config.description,
          image: config.image,
          order_id: orderData.order_id,
          handler: async (response) => {
            try {
              // Verify payment
              await axios.post(
                `${API}/api/payments/verify`,
                {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  enrollment_id: enrollmentId
                },
                { headers }
              );

              setPaymentStatus('success');
              toast.success('Payment successful! Your dashboard is now unlocked.');
              onSuccess?.({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                amount: orderData.amount
              });
            } catch (verifyError) {
              setPaymentStatus('error');
              toast.error('Payment verification failed. Please contact support.');
              onError?.(verifyError);
            }
          },
          prefill: {
            name: user?.name || '',
            email: user?.email || '',
            contact: user?.phone || ''
          },
          theme: config.theme,
          modal: {
            ondismiss: () => {
              setLoading(false);
              toast.info('Payment cancelled');
            }
          }
        };

        const razorpayInstance = new Razorpay(options);
        razorpayInstance.on('payment.failed', (response) => {
          setPaymentStatus('error');
          toast.error(`Payment failed: ${response.error.description}`);
          onError?.(response.error);
        });
        razorpayInstance.open();
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('error');
      toast.error(error.response?.data?.detail || error.message || 'Payment failed');
      onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [amount, courseId, tier, enrollmentId, couponCode, Razorpay, onSuccess, onError, user]);

  const getButtonContent = () => {
    if (loading) {
      return (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Processing...
        </>
      );
    }
    if (paymentStatus === 'success') {
      return (
        <>
          <CheckCircle className="w-4 h-4 mr-2" />
          Payment Complete
        </>
      );
    }
    if (paymentStatus === 'error') {
      return (
        <>
          <AlertCircle className="w-4 h-4 mr-2" />
          Retry Payment
        </>
      );
    }
    return (
      <>
        <CreditCard className="w-4 h-4 mr-2" />
        {buttonText}
      </>
    );
  };

  return (
    <Button
      onClick={handlePayment}
      disabled={disabled || loading || paymentStatus === 'success'}
      className={`${className} ${paymentStatus === 'success' ? 'bg-green-600 hover:bg-green-700' : ''}`}
      data-testid="payment-button"
    >
      {getButtonContent()}
    </Button>
  );
}

/**
 * PaymentSummary Component
 * Shows payment summary with discounts
 */
export function PaymentSummary({ 
  originalAmount, 
  discountAmount = 0, 
  couponCode = null,
  tier = 'self_learning'
}) {
  const finalAmount = originalAmount - discountAmount;
  
  return (
    <div className="bg-muted/50 rounded-lg p-4 space-y-2" data-testid="payment-summary">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Course Price</span>
        <span>₹{originalAmount.toLocaleString('en-IN')}</span>
      </div>
      
      {discountAmount > 0 && (
        <div className="flex justify-between text-sm text-green-600">
          <span>Discount {couponCode && `(${couponCode})`}</span>
          <span>-₹{discountAmount.toLocaleString('en-IN')}</span>
        </div>
      )}
      
      <div className="border-t border-border pt-2 mt-2">
        <div className="flex justify-between font-semibold">
          <span>Total</span>
          <span className="text-primary">₹{finalAmount.toLocaleString('en-IN')}</span>
        </div>
      </div>
      
      <p className="text-xs text-muted-foreground mt-2">
        {tier === 'instructor_led' 
          ? 'Includes live mentorship and personalized feedback'
          : 'Self-paced learning with community support'}
      </p>
    </div>
  );
}
